import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { ORDER_URL } from "@/components/Navbar";

const dishes = [
  {
    name: "Chicken Biryani",
    src: "https://images.unsplash.com/photo-1589302168068-964664d93dc0?q=80&w=1200&auto=format&fit=crop",
    span: "col-span-2 row-span-2",
  },
  {
    name: "Charcoal Kebabs",
    src: "https://images.unsplash.com/photo-1529193591184-b1d58069ecdd?q=80&w=1200&auto=format&fit=crop",
    span: "",
  },
  {
    name: "Butter Chicken",
    src: "https://images.unsplash.com/photo-1708782344490-9026aaa5eec7?q=80&w=1200&auto=format&fit=crop",
    span: "",
  },
  {
    name: "Garlic Naan",
    src: "https://images.unsplash.com/photo-1697155406014-04dc649b0953?q=80&w=1200&auto=format&fit=crop",
    span: "",
  },
  {
    name: "Tandoori Chicken",
    src: "https://images.unsplash.com/photo-1610057099443-fde8c4d50f91?q=80&w=1200&auto=format&fit=crop",
    span: "col-span-2",
  },
  {
    name: "Slow-Cooked Curry",
    src: "https://images.unsplash.com/photo-1565557623262-b51c2513a641?q=80&w=1200&auto=format&fit=crop",
    span: "",
  },
  {
    name: "Chicken Karahi",
    src: "https://images.unsplash.com/photo-1603894584373-5ac82b2ae398?q=80&w=1200&auto=format&fit=crop",
    span: "",
  },
  {
    name: "Saffron Rice",
    src: "https://images.unsplash.com/photo-1563379091339-03b21ab4a4f8?q=80&w=1200&auto=format&fit=crop",
    span: "col-span-2",
  },
];

const DishCard = ({ dish, index }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <motion.figure
      ref={ref}
      data-testid={`gallery-item-${index}`}
      initial={{ opacity: 0, y: 40, scale: 0.97 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ delay: (index % 4) * 0.08, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`group relative rounded-2xl overflow-hidden min-h-[220px] ${dish.span}`}
    >
      <motion.img
        src={dish.src}
        alt={dish.name}
        loading="lazy"
        className="absolute inset-0 h-[116%] w-full object-cover transition-[transform] duration-700 group-hover:scale-105"
        style={{ y }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128]/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <figcaption className="absolute bottom-0 left-0 right-0 p-5 flex items-center justify-between translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-[transform,opacity] duration-500">
        <span className="font-heading text-xl sm:text-2xl font-semibold text-cream">{dish.name}</span>
        <a
          data-testid={`gallery-order-${index}`}
          href={ORDER_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Order ${dish.name} online`}
          className="text-gold"
        >
          <ArrowUpRight size={22} />
        </a>
      </figcaption>
    </motion.figure>
  );
};

const Gallery = () => (
  <section id="gallery" data-testid="gallery-section" className="bg-beige py-24 sm:py-36">
    <div className="max-w-7xl mx-auto px-6 sm:px-10">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="font-script text-spice text-2xl sm:text-3xl mb-3"
          >
            from the kitchen
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1, duration: 0.7 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-navy"
          >
            A feast for the eyes first.
          </motion.h2>
        </div>
        <motion.a
          data-testid="gallery-view-menu-btn"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          href={ORDER_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="group flex items-center gap-2 text-navy font-semibold border-b-2 border-gold pb-1 hover:text-spice transition-colors duration-300"
        >
          View the full menu
          <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
        </motion.a>
      </div>

      <div className="mt-14 grid grid-cols-2 md:grid-cols-4 auto-rows-[180px] sm:auto-rows-[220px] gap-4 sm:gap-5">
        {dishes.map((dish, i) => (
          <DishCard key={dish.name} dish={dish} index={i} />
        ))}
      </div>
    </div>
  </section>
);

export default Gallery;
